import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Input } from "@material-tailwind/react";
import { toast } from "react-toastify";
import { IconPicker, IconRenderer, isIconValue } from "@/features/shared/components/IconPicker";
import Modal from "@/features/shared/components/Modal/Modal";
const DEFAULT_FORM_STATE = {
    id: undefined,
    code: "",
    label: "",
    indic_display: true,
    indic_relance: false,
    color: "",
    icon_svg: "",
    sequence: "",
    destination_states: "",
    source_states: "",
};
const DEFAULT_COLOR_PICKER_VALUE = "#4b5563";
const StateForm = ({ initialData, onSubmit, onCancel }) => {
    const [formState, setFormState] = useState({
        ...DEFAULT_FORM_STATE,
        id: initialData?.id,
        code: initialData?.code ?? DEFAULT_FORM_STATE.code,
        label: initialData?.label ?? DEFAULT_FORM_STATE.label,
        indic_display: initialData?.indic_display ?? DEFAULT_FORM_STATE.indic_display,
        indic_relance: initialData?.indic_relance ?? DEFAULT_FORM_STATE.indic_relance,
        color: initialData?.color ?? "",
        icon_svg: initialData?.icon_svg ?? "",
        sequence: initialData && typeof initialData.display_sequence === "number"
            ? String(initialData.display_sequence)
            : "",
        destination_states: initialData?.destination_states?.join(", ") ?? "",
        source_states: initialData?.source_states?.join(", ") ?? "",
    });
    const [errors, setErrors] = useState({});
    const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
    const iconValue = isIconValue(formState.icon_svg) ? formState.icon_svg : undefined;
    const legacyIconMarkup = formState.icon_svg && !iconValue ? formState.icon_svg : undefined;
    useEffect(() => {
        setFormState({
            ...DEFAULT_FORM_STATE,
            id: initialData?.id,
            code: initialData?.code ?? DEFAULT_FORM_STATE.code,
            label: initialData?.label ?? DEFAULT_FORM_STATE.label,
            indic_display: initialData?.indic_display ?? DEFAULT_FORM_STATE.indic_display,
            indic_relance: initialData?.indic_relance ?? DEFAULT_FORM_STATE.indic_relance,
            color: initialData?.color ?? "",
            icon_svg: initialData?.icon_svg ?? "",
            sequence: initialData && typeof initialData.display_sequence === "number"
                ? String(initialData.display_sequence)
                : "",
            destination_states: initialData?.destination_states?.join(", ") ?? "",
            source_states: initialData?.source_states?.join(", ") ?? "",
        });
    }, [initialData]);
    const handleChange = (e) => {
        const { name, value, type } = e.target;
        // Gestion spécifique pour les checkboxes
        const newValue = type === "checkbox" ? e.target.checked : value;
        if (name === "sequence") {
            setFormState((prev) => ({
                ...prev,
                sequence: typeof newValue === "string" ? newValue : prev.sequence,
            }));
            return;
        }
        setFormState((prev) => ({ ...prev, [name]: newValue }));
    };
    const validateFields = () => {
        const newErrors = {};
        if (!formState.code.trim()) {
            newErrors.code = "Le code est obligatoire.";
        }
        else if (formState.code.length < 3) {
            newErrors.code = "Le code doit contenir au moins 3 caractères.";
        }
        if (!formState.label.trim()) {
            newErrors.label = "Le libellé est obligatoire.";
        }
        else if (formState.label.length < 3) {
            newErrors.label = "Le libellé doit contenir au moins 3 caractères.";
        }
        if (formState.sequence.trim() && Number.isNaN(Number(formState.sequence))) {
            newErrors.sequence = "L'ordre d'affichage doit être un nombre valide.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleColorChange = (event) => {
        const { value } = event.target;
        setFormState((prev) => ({ ...prev, color: value }));
    };
    const resetColor = () => {
        setFormState((prev) => ({ ...prev, color: "" }));
    };
    const handleIconChange = (icon) => {
        setFormState((prev) => ({ ...prev, icon_svg: icon }));
    };
    const handleIconSelect = (icon) => {
        handleIconChange(icon);
        setIsIconPickerOpen(false);
    };
    const openIconPicker = () => {
        setIsIconPickerOpen(true);
    };
    const closeIconPicker = () => {
        setIsIconPickerOpen(false);
    };
    const resetIcon = () => {
        setFormState((prev) => ({ ...prev, icon_svg: "" }));
    };
    const handleSubmit = () => {
        if (!validateFields()) {
            return;
        }
        try {
            const sanitizeList = (value) => {
                if (!value.trim()) {
                    return undefined;
                }
                return value
                    .split(",")
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0);
            };
            const sanitizedDestination = sanitizeList(formState.destination_states);
            const sanitizedSource = sanitizeList(formState.source_states);
            const sanitizedSequence = formState.sequence.trim();
            const sanitizedColor = formState.color.trim();
            const sanitizedIcon = formState.icon_svg.trim();
            const sanitizedData = {
                id: formState.id,
                code: formState.code.trim(),
                label: formState.label.trim(),
                indic_display: formState.indic_display,
                indic_relance: formState.indic_relance,
            };
            if (sanitizedSequence !== "") {
                sanitizedData.sequence = Number(sanitizedSequence);
            }
            if (sanitizedColor) {
                sanitizedData.color = sanitizedColor;
            }
            else if (initialData?.color) {
                sanitizedData.color = null;
            }
            if (sanitizedIcon) {
                sanitizedData.icon_svg = sanitizedIcon;
            }
            else if (initialData?.icon_svg) {
                sanitizedData.icon_svg = null;
            }
            if (sanitizedDestination !== undefined) {
                sanitizedData.destination_states = sanitizedDestination;
            }
            else if (initialData?.destination_states && initialData.destination_states.length > 0) {
                sanitizedData.destination_states = [];
            }
            if (sanitizedSource !== undefined) {
                sanitizedData.source_states = sanitizedSource;
            }
            else if (initialData?.source_states && initialData.source_states.length > 0) {
                sanitizedData.source_states = [];
            }
            onSubmit(sanitizedData);
        }
        catch (error) {
            toast.error(`Erreur : ${error.message}`);
        }
    };
    return (_jsxs("div", { className: "bg-white w-full", children: [_jsxs("div", { className: "flex flex-col md:flex-row justify-between items-start md:items-center border-b p-4 bg-gray-100", children: [initialData && (_jsxs("h1", { className: "text-l font-bold", children: [_jsx("span", { className: "text-blue-900", children: initialData.label }), ":"] })), !initialData && (_jsx("h1", { className: "text-l font-bold", children: _jsx("span", { className: "text-blue-900", children: "Ajouter un nouvel \u00E9tat" }) }))] }), _jsxs("form", { className: "flex flex-col gap-4 p-2", children: [_jsx("div", { className: "pt-2", children: _jsx(Input, { variant: "standard", label: "Code", value: formState.code, type: "text", name: "code", placeholder: "Code", onChange: handleChange, className: "w-full", crossOrigin: "" }) }), errors.code && (_jsx("p", { className: "text-sm text-red-500 mt-1", children: errors.code })), _jsx("div", { className: "pt-2", children: _jsx(Input, { variant: "standard", label: "Libell\u00E9", value: formState.label, type: "text", name: "label", placeholder: "Libell\u00E9", onChange: handleChange, className: "w-full", crossOrigin: "" }) }), errors.label && (_jsx("p", { className: "text-sm text-red-500 mt-1", children: errors.label })), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { className: "pt-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", htmlFor: "color", children: "Couleur" }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("input", { id: "color", type: "color", name: "color", value: formState.color || DEFAULT_COLOR_PICKER_VALUE, onChange: handleColorChange, className: "h-10 w-14 cursor-pointer rounded border border-gray-300 bg-transparent p-1 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-sm text-gray-600", children: formState.color ? formState.color.toUpperCase() : "Aucune couleur sélectionnée" }), formState.color && (_jsx("button", { type: "button", onClick: resetColor, className: "text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline", children: "R\u00E9initialiser la couleur" }))] })] })] }), _jsxs("div", { className: "pt-2", children: [_jsx(Input, { variant: "standard", label: "Ordre d'affichage", value: formState.sequence, type: "number", name: "sequence", placeholder: "1", onChange: handleChange, className: "w-full", crossOrigin: "" }), errors.sequence && (_jsx("p", { className: "text-sm text-red-500 mt-1", children: errors.sequence }))] })] }), _jsxs("div", { className: "pt-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", htmlFor: "icon-picker", children: "Ic\u00F4ne" }), _jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("button", { type: "button", onClick: openIconPicker, className: "flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition hover:border-blue-300 hover:bg-blue-50", children: [iconValue ? (_jsxs(_Fragment, { children: [_jsx(IconRenderer, { value: iconValue, size: 24, className: "text-blue-600" }), _jsx("code", { className: "rounded bg-white px-2 py-1 text-xs font-medium text-gray-700 sm:text-sm", children: iconValue })] })) : legacyIconMarkup ? (_jsx("span", { className: "text-sm text-gray-700", dangerouslySetInnerHTML: { __html: legacyIconMarkup } })) : (_jsx("span", { className: "text-sm text-gray-500", children: "Aucune ic\u00F4ne s\u00E9lectionn\u00E9e" })), _jsx("span", { className: "ml-auto text-xs font-semibold text-blue-600", children: "Choisir une ic\u00F4ne" })] }), (iconValue || legacyIconMarkup) && (_jsx("button", { type: "button", onClick: resetIcon, className: "self-start text-xs font-medium text-blue-600 hover:underline", children: "Retirer l'ic\u00F4ne" })), legacyIconMarkup && !iconValue && (_jsx("p", { className: "text-xs text-gray-500", children: "Ic\u00F4ne h\u00E9rit\u00E9e d\u00E9tect\u00E9e. S\u00E9lectionnez une nouvelle ic\u00F4ne pour adopter le nouveau format." }))] })] }), isIconPickerOpen && (_jsx(Modal, { onClose: closeIconPicker, children: _jsxs("div", { className: "rounded-lg bg-white p-4 shadow-lg", children: [_jsxs("div", { className: "mb-4 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-800", children: "S\u00E9lectionner une ic\u00F4ne" }), _jsx("button", { type: "button", onClick: closeIconPicker, className: "text-sm font-medium text-blue-600 hover:underline", children: "Fermer" })] }), _jsx(IconPicker, { value: iconValue, onChange: handleIconSelect })] }) })), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { className: "pt-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", htmlFor: "destination_states", children: "\u00C9tats de destination autoris\u00E9s (s\u00E9par\u00E9s par une virgule)" }), _jsx("textarea", { id: "destination_states", name: "destination_states", value: formState.destination_states, onChange: handleChange, placeholder: "IN_PROGRESS, DONE", className: "w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none p-2", rows: 3 })] }), _jsxs("div", { className: "pt-2", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", htmlFor: "source_states", children: "\u00C9tats sources autoris\u00E9s (s\u00E9par\u00E9s par une virgule)" }), _jsx("textarea", { id: "source_states", name: "source_states", value: formState.source_states, onChange: handleChange, placeholder: "TODO, ARCHIVED", className: "w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none p-2", rows: 3 })] })] }), _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6", children: [_jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", name: "indic_display", checked: formState.indic_display, onChange: handleChange, className: "w-5 h-5" }), _jsx("span", { children: "Indice d'affichage" })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", name: "indic_relance", checked: formState.indic_relance, onChange: handleChange, className: "w-5 h-5" }), _jsx("span", { children: "Relan\u00E7able" })] })] }), _jsxs("div", { className: "mt-6 flex flex-col sm:flex-row justify-end gap-4 p-2", children: [_jsx("button", { type: "button", onClick: onCancel, className: "bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600", children: "Annuler" }), _jsx("button", { type: "button", onClick: handleSubmit, className: "bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600", children: "Sauvegarder" })] })] })] }));
};
export default StateForm;
